import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, PLATFORM_ID, computed, effect, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CurrencyPipe, DatePipe, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BookingService } from '../../../services/booking.service';
import { WaitlistService } from '../../../services/waitlist.service';
import { AlertsService } from '../../../services/alerts.service';
import { MediaService } from '../../../services/media.service';
import { SharedService } from '../../../shared/services/shared.service';
import { AnalyticsService, AnalyticsEvent } from '../../../services/analytics.service';
import { Booking, BookingStatus } from '../../../model/booking.model';
import { AlertConfirmationData, AskQuestionModel, BookingConfirmationData, BookingReviewData, CancellationConfirmationData, PaymentBlock, PaymentOption, PaymentStatus, TeeTimeActionPayload, TeeTimeRebookAction, WaitlistConfirmationData, WaitlistPaymentType, WaitlistReviewData } from '../../../model/chat.model';
import { DatePickerModalComponent } from '../../../shared/components/date-picker-modal/date-picker-modal.component';
import { PaymentBlockComponent } from '../payment-block/payment-block.component';

@Component({
  selector: 'app-tee-time-detail',
  imports: [DatePipe, CurrencyPipe, PaymentBlockComponent],
  templateUrl: './tee-time-detail.component.html',
  styleUrl: './tee-time-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.app-tee-time-detail--embedded]': 'embedded()',
    '[class.app-tee-time-detail--prepay-active]': 'prepayActive()',
  },
})
export class TeeTimeDetailComponent implements OnInit {
  private readonly _router = inject(Router);
  private readonly _bookingService = inject(BookingService);
  private readonly _waitlistService = inject(WaitlistService);
  private readonly _alertsService = inject(AlertsService);
  private readonly _sharedService = inject(SharedService);
  private readonly _modal = inject(NgbModal);
  private readonly _analytics = inject(AnalyticsService);
  private readonly _mediaService = inject(MediaService);
  private readonly _isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly id = input<string | undefined>(undefined);
  /** Bound from the `?type=` query param (booking | waitlist | alert) so a direct/refreshed
   *  URL knows which endpoint to fetch from without relying on router state or cache. */
  readonly type = input<'booking' | 'waitlist' | 'alert' | undefined>(undefined);
  readonly review = input<BookingReviewData | undefined>(undefined);
  readonly confirmation = input<BookingConfirmationData | undefined>(undefined);
  readonly cancellation = input<CancellationConfirmationData | undefined>(undefined);
  readonly waitlistReview = input<WaitlistReviewData | undefined>(undefined);
  readonly waitlistConfirmation = input<WaitlistConfirmationData | undefined>(undefined);
  readonly alertConfirmation = input<AlertConfirmationData | undefined>(undefined);
  readonly embedded = input<boolean>(false);
  /** When false, all interactive controls are disabled — used for stale chat messages */
  readonly interactive = input<boolean>(true);
  readonly action = output<TeeTimeActionPayload>();

  /** Local lock to prevent double-submit between click and the message becoming non-last */
  protected readonly submitted = signal(false);
  protected readonly loading = signal(false);

  // ── Prepay (booking-review) ────────────────────────────────────────────────
  /**
   * Payment methods offered on booking-review (radio buttons). Driven entirely by the backend's
   * `costSummary.paymentOptions`, which already gates the prepay option on course support
   * (`available: false` when the course is payonsite-only). Empty when no options are sent — the
   * template then falls back to pay-at-course only.
   */
  protected readonly paymentOptions = computed<PaymentOption[]>(
    () => this.review()?.costSummary?.paymentOptions ?? [],
  );
  /** id of the currently-selected payment option; null until a default is resolved. */
  private readonly _selectedPaymentId = signal<string | null>(null);
  protected readonly selectedPaymentId = computed<string | null>(() => {
    const explicit = this._selectedPaymentId();
    if (explicit) return explicit;
    const opts = this.paymentOptions();
    return (opts.find((o) => o.selected && o.available) ?? opts.find((o) => o.available))?.id ?? null;
  });

  // ── Prepay (booking-confirmation) ──────────────────────────────────────────
  /** Live payment block, updated by the PaymentBlock poll loop (overrides the seed payload). */
  private readonly _livePayment = signal<PaymentBlock | undefined>(undefined);
  /** confirmationNumber captured once TeeFox confirms payment. */
  private readonly _liveConfirmationNumber = signal<string | null | undefined>(undefined);
  /** The payment block to render — from the backend booking-confirmation `payment` block. */
  protected readonly paymentBlock = computed<PaymentBlock | undefined>(
    () => this.confirmation()?.payment,
  );
  /** Effective payment status for a confirmation widget (undefined when not a prepay booking). */
  protected readonly paymentStatus = computed<PaymentStatus | undefined>(() => {
    const live = this._livePayment()?.status;
    const seed = this.paymentBlock()?.status;
    const status = live ?? seed;
    return status && status !== 'not_applicable' ? status : undefined;
  });
  /** Render the inline payment surface for a prepay confirmation. */
  protected readonly showPaymentBlock = computed<boolean>(
    () => this.isConfirmationMode() && this.paymentStatus() !== undefined,
  );
  /**
   * True while a prepay booking hasn't been paid yet (pending / expired / cancelled). In this
   * state we collapse the confirmation card to just the payment gateway — the cost summary,
   * "What's next", and IDs are suppressed until payment succeeds (then the full confirmed card
   * shows) or fails (then the terminal card shows).
   */
  protected readonly prepayActive = computed<boolean>(() => {
    const s = this.paymentStatus();
    return s !== undefined && s !== 'paid';
  });

  protected readonly booking = computed<Booking | undefined>(() => {
    const cancellationData = this.cancellation();
    if (cancellationData) {
      return this.mapCancellationToBooking(cancellationData);
    }
    const alertConfirmationData = this.alertConfirmation();
    if (alertConfirmationData) {
      return this.mapAlertConfirmationToBooking(alertConfirmationData);
    }
    const waitlistConfirmationData = this.waitlistConfirmation();
    if (waitlistConfirmationData) {
      return this.mapWaitlistConfirmationToBooking(waitlistConfirmationData);
    }
    const confirmationData = this.confirmation();
    if (confirmationData) {
      const mapped = this.mapConfirmationToBooking(confirmationData);
      const liveCode = this._liveConfirmationNumber();
      return liveCode ? { ...mapped, confirmationCode: liveCode } : mapped;
    }
    const waitlistReviewData = this.waitlistReview();
    if (waitlistReviewData) {
      return this.mapWaitlistReviewToBooking(waitlistReviewData);
    }
    const reviewData = this.review();
    if (reviewData) {
      return this.mapReviewToBooking(reviewData);
    }
    const bookingId = this.id();
    if (!bookingId) return undefined;
    const cached =
      this._bookingService.getById(bookingId)
      ?? this._waitlistService.getById(bookingId)
      ?? this._alertsService.getById(bookingId);
    // Alert/waitlist rows are also present in the unified bookings cache (listing feed),
    // but the richer per-type detail lives in their own service once loadById fetches it.
    if (cached?.isAlert) {
      return this._alertsService.getById(bookingId) ?? cached;
    }
    if (cached?.isWaitlist) {
      return this._waitlistService.getById(bookingId) ?? cached;
    }
    return cached;
  });

  // ── Contact avatar ─────────────────────────────────────────────────────────
  /**
   * The backend sends the contact's avatar as a media *id* (not a URL), so binding it directly
   * renders a broken image. We resolve the id to a signed URL via MediaService and cache it here.
   */
  private readonly _resolvedAvatarUrl = signal<string | null>(null);
  /** Guards against re-fetching the same id when the effect re-runs. */
  private _lastAvatarId: string | null = null;
  /** Live subscription re-signing the avatar URL before it expires. */
  private _avatarSub: Subscription | null = null;
  private readonly _destroyRef = inject(DestroyRef);
  /**
   * URLs the browser failed to load — e.g. the backend embedded an already-expired presigned
   * URL directly in the payload, which we can't re-sign because we never saw the media id.
   * Each failure drops to the next candidate, and finally to the default icon, instead of
   * leaving a broken image box on screen.
   */
  private readonly _failedAvatarUrls = signal<ReadonlySet<string>>(new Set<string>());
  /**
   * The signed-in user. "Your details" always describes the current user, but several payloads
   * (cancellation, booking/waitlist confirmation) come back without an avatar on the contact,
   * so the locally held — and auto re-signed — profile picture is the fallback (CGA-3121).
   */
  private readonly _currentUser = toSignal(this._sharedService.user$, { initialValue: undefined });
  /** URL to render for the "Your details" avatar: the payload URL if it already had one, else the
   *  resolved media URL, else the signed-in user's own picture. Null renders the default icon. */
  protected readonly contactAvatarUrl = computed<string | null>(() => {
    const failed = this._failedAvatarUrls();
    const raw = this.booking()?.contact?.avatarUrl;
    const fromBooking = raw && /^https?:\/\//i.test(raw) ? raw : this._resolvedAvatarUrl();
    const candidates = [fromBooking, this._currentUser()?.imageUrl];
    return candidates.find((url): url is string => !!url && !failed.has(url)) ?? null;
  });

  protected onAvatarError(url: string): void {
    if (this._failedAvatarUrls().has(url)) return;
    this._failedAvatarUrls.set(new Set(this._failedAvatarUrls()).add(url));
  }

  constructor() {
    // Resolve the contact's avatar media id → URL whenever the booking data (and thus the id)
    // changes. Handles both the embedded chat card (data available synchronously) and route mode
    // (booking populated asynchronously by loadById).
    effect(() => {
      const raw = this.booking()?.contact?.avatarUrl;
      if (!raw || /^https?:\/\//i.test(raw)) {
        // No media id to resolve — drop any URL resolved for a previous booking so it can't
        // leak into this card.
        this._avatarSub?.unsubscribe();
        this._lastAvatarId = null;
        this._resolvedAvatarUrl.set(null);
        return;
      }
      if (!this._isBrowser) return;
      if (raw === this._lastAvatarId) return;
      this._lastAvatarId = raw;
      this._avatarSub?.unsubscribe();
      // watchMedia re-signs the URL shortly before it expires, so a confirmation card left
      // on screen (or scrolled back to in chat history) never renders a dead S3 link.
      // takeUntilDestroyed ends the watch on destroy; the manual unsubscribe above only
      // handles switching to a new avatar id while the component is alive.
      this._avatarSub = this._mediaService.watchMedia(raw)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: (media) => {
            if (media.url) {
              // this._avatarFailed.set(false);
              this._resolvedAvatarUrl.set(media.url);
            }
          },
          error: () => { /* leave the default icon in place */ },
        });
    });
  }

  protected readonly isReviewMode = computed(() => this.review() !== undefined);
  protected readonly isConfirmationMode = computed(() => this.confirmation() !== undefined);
  protected readonly isCancellationMode = computed(() => this.cancellation() !== undefined);
  protected readonly isWaitlistReviewMode = computed(() => this.waitlistReview() !== undefined);
  protected readonly isWaitlistConfirmationMode = computed(() => this.waitlistConfirmation() !== undefined);
  protected readonly isAlertConfirmationMode = computed(() => this.alertConfirmation() !== undefined);
  protected readonly headerTitle = computed(() => {
    if (this.isCancellationMode()) return 'Booking cancelled';
    if (this.isAlertConfirmationMode()) {
      const a = this.alertConfirmation();
      if (a?.statusLabel) return this.normalizeStatusLabel(a.statusLabel);
      switch (a?.status) {
        case 'notified': return 'Slot opened';
        case 'expired': return 'Alert expired';
        case 'cancelled': return 'Alert cancelled';
        case 'notify_failed': return 'Alert expired';
        case 'processing': return 'Saving alert';
        default: return "You'll be alerted";
      }
    }
    if (this.isWaitlistConfirmationMode()) {
      const c = this.waitlistConfirmation();
      if (c?.statusLabel) return this.normalizeStatusLabel(c.statusLabel);
      switch (c?.status) {
        case 'confirmed': return 'Tee time confirmed';
        case 'expired': return 'Waitlist expired';
        case 'canceled_by_user':
        case 'cancelled': return 'Waitlist cancelled';
        case 'auto_confirm_failed': return 'Waitlist expired';
        case 'failed': return 'Waitlist failed';
        case 'processing': return 'Adding to waitlist';
        default: return "You're on the waitlist";
      }
    }
    if (this.isConfirmationMode()) {
      const pay = this.paymentStatus();
      if (pay) {
        switch (pay) {
          case 'pending': return 'Pending — pay to confirm';
          case 'paid': return 'Booking confirmed';
          case 'expired': return 'Payment link expired';
          case 'cancelled': return 'Payment cancelled';
          case 'refunded': return 'Payment refunded';
        }
      }
      const status = this.confirmation()?.status;
      if (status === 'processing') return 'Booking processing';
      if (status === 'failed') return 'Booking failed';
      return 'Booking confirmed';
    }
    if (this.isWaitlistReviewMode()) return 'Review your waitlist';
    if (this.isReviewMode()) return 'Review your booking';
    const b = this.booking();
    if (b?.statusLabel) return this.normalizeStatusLabel(b.statusLabel);
    switch (b?.status) {
      case 'cancelled': return 'Booking cancelled';
      case 'completed': return 'Booking completed';
      case 'processing': return 'Booking processing';
      case 'waitlist': return 'On the waitlist';
      case 'booked': return 'Booking confirmed';
      default: return 'Booking summary';
    }
  });

  /** Display-only: an "Action required" status (from the API or defaults) reads as "Expired". */
  private normalizeStatusLabel(label: string): string {
    return label.trim().toLowerCase() === 'action required' ? 'Expired' : label;
  }

  protected readonly headerColor = computed(() => {
    if (this.isCancellationMode()) return 'red';
    if (this.isAlertConfirmationMode()) {
      const a = this.alertConfirmation();
      if (a?.statusColor) return a.statusColor;
      switch (a?.status) {
        case 'notified': return 'green';
        case 'expired':
        case 'cancelled': return 'grey';
        case 'notify_failed': return 'red';
        case 'processing': return 'yellow';
        default: return 'blue';
      }
    }
    if (this.isWaitlistConfirmationMode()) {
      const c = this.waitlistConfirmation();
      if (c?.statusColor) return c.statusColor;
      switch (c?.status) {
        case 'confirmed': return 'green';
        case 'expired':
        case 'cancelled':
        case 'canceled_by_user': return 'grey';
        case 'auto_confirm_failed':
        case 'failed': return 'red';
        default: return 'yellow';
      }
    }
    if (this.isWaitlistReviewMode()) return 'green';
    if (this.isReviewMode()) return 'green';
    if (this.isConfirmationMode()) {
      const pay = this.paymentStatus();
      if (pay) {
        switch (pay) {
          case 'pending': return 'yellow';
          case 'paid': return 'green';
          case 'expired':
          case 'cancelled':
          case 'refunded': return 'grey';
        }
      }
      const status = this.confirmation()?.status;
      if (status === 'failed') return 'red';
      if (status === 'processing') return 'yellow';
      return 'green';
    }
    const b = this.booking();
    if (b?.statusColor) return b.statusColor;
    switch (b?.status) {
      case 'cancelled': return 'grey';
      case 'completed': return 'grey';
      case 'processing': return 'yellow';
      case 'waitlist': return 'yellow';
      default: return 'green';
    }
  });

  /** Named modifier class to apply — empty when the color is a raw hex from the API. */
  protected readonly headerColorModifier = computed(() =>
    this.headerColor().startsWith('#') ? '' : this.headerColor()
  );

  /** Raw hex background (e.g. statusColor from the API) applied inline, mirroring the listing card. */
  protected readonly headerColorStyle = computed(() =>
    this.headerColor().startsWith('#') ? this.headerColor() : null
  );

  protected canCancelBooking(): boolean {
    return this.booking()?.status === 'booked';
  }

  ngOnInit(): void {
    if (!this.embedded()) {
      this._sharedService.backButton.set({ show: true, url: '/tee-times', text: '', action: '' });
    }
    const id = this.id();
    if (
      this._isBrowser
      && id
      && !this.review()
      && !this.confirmation()
      && !this.cancellation()
      && !this.waitlistReview()
      && !this.waitlistConfirmation()
      && !this.alertConfirmation()
    ) {
      this.loadById(id);
    }
  }

  private loadById(id: string): void {
    // The `?type=` query param (set when navigating from the listing) tells us which
    // collection the row belongs to and survives a refresh / shared link. Fall back to
    // router state, then the in-memory cache, then the bookings→waitlists→alerts chain.
    const typeHint = this.type();
    const stateHint =
      typeof history !== 'undefined'
        ? (history.state as { isWaitlist?: boolean; isAlert?: boolean } | undefined)
        : undefined;
    const fromCache =
      this._bookingService.getById(id)
      ?? this._waitlistService.getById(id)
      ?? this._alertsService.getById(id);
    const isAlert =
      typeHint === 'alert' || stateHint?.isAlert === true || fromCache?.isAlert === true;
    const isWaitlist =
      typeHint === 'waitlist' || stateHint?.isWaitlist === true || fromCache?.isWaitlist === true;

    this.loading.set(true);
    if (isAlert) {
      this._alertsService.loadAlertById(id)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: () => this.loading.set(false),
          error: (err) => {
            console.error('Failed to load alert', err);
            this.loading.set(false);
          },
        });
      return;
    }
    if (isWaitlist) {
      this._waitlistService.loadWaitlistById(id)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: () => this.loading.set(false),
          error: (err) => {
            console.error('Failed to load waitlist', err);
            this.loading.set(false);
          },
        });
      return;
    }
    this._bookingService.loadBookingById(id)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this.loading.set(false),
        error: () => {
          // Direct visit and bookings/:id 404'd — try waitlists, then alerts.
          this._waitlistService.loadWaitlistById(id)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
              next: () => this.loading.set(false),
              error: () => {
                this._alertsService.loadAlertById(id)
                  .pipe(takeUntilDestroyed(this._destroyRef))
                  .subscribe({
                    next: () => this.loading.set(false),
                    error: (err) => {
                      console.error('Failed to load booking, waitlist, or alert', err);
                      this.loading.set(false);
                    },
                  });
              },
            });
        },
      });
  }

  confirmBooking(): void {
    if (this.submitted()) return;
    const r = this.review();
    if (!r) return;
    this.submitted.set(true);
    const sel = r.selection;
    const pricePerPatron =
      sel?.pricePerPatron ??
      r.pricePerPatron ??
      Math.round((r.costSummary.perPlayer[0]?.amount ?? 0) * 100);
    const requestType: 'book_payonsite' | 'book_prepay' =
      this.selectedPaymentId() === 'book_prepay' ? 'book_prepay' : 'book_payonsite';
    this._analytics.track(AnalyticsEvent.BOOKING_CONFIRMED, {
      course_name: sel?.courseName ?? r.course.name,
      appointment_time: sel?.appointmentTime ?? r.teeTime.apptTime,
      players: sel?.players ?? r.teeTime.players,
      holes: sel?.holes ?? r.teeTime.holes,
      price_per_patron: pricePerPatron,
      request_type: requestType,
    });
    this.action.emit({
      question: 'Confirm booking',
      action: {
        type: 'confirm_booking',
        courseName: sel?.courseName ?? r.course.name,
        locationId: sel?.locationId ?? r.course.locationId,
        appointmentTime: sel?.appointmentTime ?? r.teeTime.apptTime,
        players: sel?.players ?? r.teeTime.players,
        holes: sel?.holes ?? r.teeTime.holes,
        pricePerPatron,
        requestType,
        // Echo the course capability so the BE can guard: it downgrades book_prepay →
        // book_payonsite whenever the course doesn't support prepay.
        ...(sel?.supportedRequestTypes ? { supportedRequestTypes: sel.supportedRequestTypes } : {}),
        ...(sel?.place ? { place: sel.place } : {}),
        ...(sel?.coordinates ? { coordinates: sel.coordinates } : {}),
        ...(sel?.previousBookingId ? { previousBookingId: sel.previousBookingId } : {}),
      },
    });
  }

  /** Radio selection on booking-review. Ignored for unavailable options. */
  protected selectPaymentOption(id: string): void {
    if (!this.interactive()) return;
    const opt = this.paymentOptions().find((o) => o.id === id);
    if (opt && opt.available) this._selectedPaymentId.set(id);
  }

  /** PaymentBlock reported a status transition (poll / postMessage) — reflect it in the header. */
  protected onPaymentChange(payment: PaymentBlock): void {
    this._livePayment.set(payment);
  }

  /** PaymentBlock reached a terminal state — capture the confirmation number for the paid UI. */
  protected onPaymentResolved(event: { status: PaymentStatus; confirmationNumber?: string | null }): void {
    if (event.confirmationNumber) this._liveConfirmationNumber.set(event.confirmationNumber);
  }

  /**
   * "Retry payment" on an expired/failed prepay card. The hold is gone once expired, so this
   * re-initiates a fresh prepay booking for the same slot — the BE places a new hold and returns
   * a new booking-confirmation with a fresh payment URL.
   */
  protected onRetryPayment(): void {
    if (this.submitted()) return;
    const c = this.confirmation();
    if (!c) return;
    this.submitted.set(true);
    const pricePerPatron = Math.round((c.costSummary?.perPlayer?.[0]?.amount ?? 0) * 100);
    this.action.emit({
      question: 'Retry payment',
      action: {
        type: 'confirm_booking',
        courseName: c.course.name,
        locationId: c.course.locationId,
        appointmentTime: c.teeTime.apptTime,
        players: c.teeTime.players,
        holes: c.teeTime.holes,
        pricePerPatron,
        requestType: 'book_prepay',
        ...(c.place ? { place: c.place } : {}),
        ...(c.coordinates ? { coordinates: c.coordinates } : {}),
      },
    });
  }

  cancelBooking(): void {
    if (this.submitted()) return;
    const c = this.confirmation();
    if (c?.bookingId && c?.referenceId) {
      this.submitted.set(true);
      this.action.emit({
        question: 'Cancel booking',
        action: {
          type: 'cancel_booking',
          bookingId: c.bookingId,
          teefoxRequestId: c.referenceId,
        },
      });
      return;
    }
    // Route-mode: dispatch through the chat (no direct REST endpoint for cancellation in v1).
    const b = this.booking();
    const bookingId = b?.bookingId;
    const teefoxRequestId = b?.referenceId;
    if (!b || !bookingId || !teefoxRequestId) return;
    this.submitted.set(true);
    const model = new AskQuestionModel();
    model.textQuestion = `Cancel booking at ${b.courseName}`;
    model.behaviour = 'clicked';
    model.showProcessingText = true;
    model.action = {
      type: 'cancel_booking',
      bookingId,
      teefoxRequestId,
    };
    this._router.navigateByUrl('/').then(() => {
      this._sharedService.setQuestion(model);
    });
  }

  protected canRemoveFromWaitlistRoute(): boolean {
    const b = this.booking();
    return !!(b?.isWaitlist && b.waitlistId && b.status === 'waitlist');
  }

  removeFromWaitlistRoute(): void {
    if (this.submitted()) return;
    const b = this.booking();
    if (!b?.waitlistId) return;
    this.submitted.set(true);
    const model = new AskQuestionModel();
    model.textQuestion = `Remove from waitlist at ${b.courseName}`;
    model.behaviour = 'clicked';
    model.showProcessingText = true;
    model.action = {
      type: 'cancel_waitlist',
      waitlistId: b.waitlistId,
    };
    this._router.navigateByUrl('/').then(() => {
      this._sharedService.setQuestion(model);
    });
  }

  protected canCancelConfirmation(): boolean {
    const c = this.confirmation();
    return !!(c?.bookingId && c?.referenceId && c?.status !== 'failed');
  }

  confirmWaitlist(): void {
    if (this.submitted()) return;
    const r = this.waitlistReview();
    if (!r) return;
    this.submitted.set(true);
    const sel = r.selection;
    this.action.emit({
      question: 'Confirm waitlist',
      action: {
        type: 'confirm_waitlist',
        courseName: sel?.courseName ?? r.course.name,
        locationId: sel?.locationId ?? r.course.locationId,
        date: sel?.date ?? r.teeTime.date,
        players: sel?.players ?? r.teeTime.players,
        holes: sel?.holes ?? r.teeTime.holes,
        requestType: 'book_payonsite',
      },
    });
  }

  cancelWaitlist(): void {
    if (this.submitted()) return;
    const c = this.waitlistConfirmation();
    const sel = c?.selection;
    const waitlistId = sel?.waitlistId ?? c?.waitlistId;
    if (!waitlistId) return;
    const teefoxRequestId = sel?.teefoxRequestId ?? c?.referenceId;
    this.submitted.set(true);
    this.action.emit({
      question: 'Cancel waitlist',
      action: {
        type: 'cancel_waitlist',
        waitlistId,
        ...(teefoxRequestId ? { teefoxRequestId } : {}),
      },
    });
  }

  protected waitlistPaymentLabel(t: WaitlistPaymentType): string {
    switch (t) {
      case 'full_at_course': return 'Pay full at course';
      case 'booking_fee_at_course': return 'Pay booking fee at course';
      case 'full_prepay': return 'Pay full now';
      case 'booking_fee_prepay': return 'Pay booking fee now';
      default: return t;
    }
  }

  protected canCancelAlert(): boolean {
    const a = this.alertConfirmation();
    if (!a) return false;
    const alertId = a.selection?.alertId ?? a.alertId;
    if (!alertId) return false;
    return a.status === 'active' || a.status === 'processing';
  }

  cancelAlert(): void {
    if (this.submitted()) return;
    const a = this.alertConfirmation();
    const alertId = a?.selection?.alertId ?? a?.alertId;
    if (!alertId) return;
    this.submitted.set(true);
    this.action.emit({
      question: 'Cancel alert',
      action: {
        type: 'cancel_alert',
        alertId,
      },
    });
  }

  protected canCancelAlertRoute(): boolean {
    const b = this.booking();
    // Route mode mirrors the chat-embedded canCancelAlert — only an active alert can be cancelled.
    // The alerts service maps status='active' → BookingStatus 'waitlist' and 'processing' stays as 'processing'.
    return !!(b?.isAlert && b.alertId && (b.status === 'waitlist' || b.status === 'processing'));
  }

  cancelAlertRoute(): void {
    if (this.submitted()) return;
    const b = this.booking();
    const alertId = b?.alertId;
    if (!alertId) return;
    this.submitted.set(true);
    const model = new AskQuestionModel();
    model.textQuestion = `Cancel alert at ${b.courseName}`;
    model.behaviour = 'clicked';
    model.showProcessingText = true;
    model.action = { type: 'cancel_alert', alertId };
    this._router.navigateByUrl('/').then(() => {
      this._sharedService.setQuestion(model);
    });
  }

  protected canCancelWaitlist(): boolean {
    const c = this.waitlistConfirmation();
    if (!c) return false;
    const waitlistId = c.selection?.waitlistId ?? c.waitlistId;
    if (!waitlistId) return false;
    // Only an active waitlisted entry can be cancelled. Confirmed/expired/cancelled/failed are terminal.
    return c.status === 'waitlisted' || c.status === 'processing';
  }

  bookAgain(): void {
    const b = this.booking();
    if (!b) return;
    const today = this.startOfToday();
    const initialDate = b.date.getTime() < today.getTime() ? today : b.date;
    const modalRef = this._modal.open(DatePickerModalComponent, {
      centered: true,
      windowClass: 'date-picker-modal-window',
    });
    const instance = modalRef.componentInstance as DatePickerModalComponent;
    instance.title = 'Book again';
    instance.confirmText = 'Search tee times';
    instance.initialDate = initialDate;
    instance.minDate = today;
    modalRef.result
      .then((picked: Date | undefined) => {
        if (!picked) return;
        this.dispatchRebook(b, picked);
      })
      .catch(() => { /* dismissed */ });
  }

  private dispatchRebook(b: Booking, date: Date): void {
    const newDateIso = this.toIsoDate(date);
    // Fall back to `b.id` — for route-mode bookings the BookingService writes the
    // Mongo _id into both `id` and `bookingId`, but for chat-embedded confirmations
    // older flows may only carry it on `id`.
    const previousBookingId = b.bookingId || b.id;
    if (!b.locationId || !previousBookingId) {
      console.warn('[rebook] cannot dispatch — missing locationId or bookingId', {
        locationId: b.locationId,
        bookingId: b.bookingId,
        id: b.id,
      });
      return;
    }

    // Swap ONLY the date prefix of the original ISO when we have it (BE does
    // string-equal exact-slot matching, so re-serialising through Date risks
    // format drift e.g. Z vs +00:00). When the raw ISO is missing, fall back to
    // constructing from b.date — the user will just see Variant B (nearby slots)
    // instead of Variant A, which is a graceful degradation.
    const originalApptTime = b.apptTime;
    const newApptTime = originalApptTime
      ? newDateIso + originalApptTime.slice(10)
      : this.buildIsoFromDate(date, b.date);

    const action: TeeTimeRebookAction = {
      type: 'rebook_tee_time',
      courseName: b.courseName,
      locationId: b.locationId,
      date: newDateIso,
      players: b.players.length,
      holes: b.holes ?? 18,
      appointmentTime: newApptTime,
      ...(originalApptTime ? { originalAppointmentTime: originalApptTime } : {}),
      preferredTime: b.time,
      previousBookingId,
    };
    const place = b.place || b.location;
    if (place) action.place = place;
    if (b.coordinates) {
      action.coordinates = { lat: b.coordinates.lat, lng: b.coordinates.lng };
    }

    const questionModel = new AskQuestionModel();
    questionModel.textQuestion = `Rebook ${b.courseName}`;
    questionModel.separation = 'rebook_tee_time';
    questionModel.showProcessingText = true;
    questionModel.behaviour = 'clicked';
    questionModel.action = action;

    // Navigate to the chat-focused root so the rebook flow lands in the user's
    // primary view. Without this, setQuestion still fires (chat-box subscribes in
    // the layout's sidebar) but on a standalone past-booking page the response
    // appears in a side panel the user often isn't looking at.
    this._router.navigateByUrl('/').then(() => {
      this._sharedService.setQuestion(questionModel);
    });
  }

  private startOfToday(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private toIsoDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  /** Used when the raw TeeFox apptTime isn't preserved on the Booking — best-effort UTC ISO derived from the original booking time-of-day applied to the new date. */
  private buildIsoFromDate(newDate: Date, originalDate: Date): string {
    const out = new Date(newDate);
    out.setUTCHours(originalDate.getUTCHours(), originalDate.getUTCMinutes(), originalDate.getUTCSeconds(), 0);
    return out.toISOString();
  }

  downloadReceipt(): void {
    // TODO: hook into receipt download once available
  }

  private mapReviewToBooking(r: BookingReviewData): Booking {
    const parsedDate = new Date(r.teeTime.apptTime);
    return {
      id: 'review',
      date: Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate,
      time: r.teeTime.time,
      status: 'booked',
      courseName: r.course.name,
      location: r.course.location,
      locationId: r.course.locationId,
      apptTime: r.teeTime.apptTime,
      players: Array.from({ length: r.teeTime.players }, () => ({})),
      holes: r.teeTime.holes,
      costLines: r.costSummary.perPlayer.map((line) => ({
        label: line.label,
        amount: line.amount,
      })),
      bookingFee: r.costSummary.bookingFee,
      totalPaid: r.costSummary.totalDue,
      contact: {
        name: r.user.name,
        email: r.user.email,
        phone: r.user.phone,
        avatarUrl: r.user.avatarUrl ?? undefined,
      },
    };
  }

  private mapCancellationToBooking(c: CancellationConfirmationData): Booking {
    const parsedDate = new Date(c.teeTime.apptTime);
    return {
      id: c.bookingId ?? 'cancellation',
      date: Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate,
      time: c.teeTime.time,
      status: 'cancelled',
      courseName: c.course.name,
      location: c.course.location,
      locationId: c.course.locationId,
      apptTime: c.teeTime.apptTime,
      bookingId: c.bookingId,
      players: Array.from({ length: c.teeTime.players }, () => ({})),
      holes: c.teeTime.holes,
      contact: c.user
        ? {
            name: c.user.name,
            email: c.user.email,
            phone: c.user.phone,
            avatarUrl: c.user.avatarUrl ?? undefined,
          }
        : undefined,
    };
  }

  private mapWaitlistReviewToBooking(r: WaitlistReviewData): Booking {
    const parsedDate = r.teeTime.apptTime ? new Date(r.teeTime.apptTime) : new Date(r.teeTime.date);
    return {
      id: 'waitlist-review',
      date: Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate,
      time: r.teeTime.time ?? 'Any time',
      status: 'waitlist',
      courseName: r.course.name,
      location: r.course.location,
      locationId: r.course.locationId,
      apptTime: r.teeTime.apptTime,
      players: Array.from({ length: r.teeTime.players }, () => ({})),
      holes: r.teeTime.holes,
      costLines: r.costSummary.perPlayer.map((line) => ({
        label: line.label,
        amount: line.amount,
      })),
      bookingFee: r.costSummary.bookingFee,
      totalPaid: r.costSummary.totalDue,
      contact: {
        name: r.user.name,
        email: r.user.email,
        phone: r.user.phone,
        avatarUrl: r.user.avatarUrl ?? undefined,
      },
    };
  }

  private mapAlertConfirmationToBooking(a: AlertConfirmationData): Booking {
    const criteria = a.criteria;
    const apptTime = criteria?.appointmentTime;
    const isoDate = criteria?.isoDate;
    const rawDate = apptTime ?? isoDate;
    const parsedDate = rawDate ? new Date(rawDate) : new Date();
    const date = Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
    const players = criteria?.players ?? 0;
    const holes = criteria?.holes;
    const timeLabel = criteria?.displayTime ?? '';
    let status: BookingStatus;
    switch (a.status) {
      case 'notified': status = 'booked'; break;
      case 'expired':
      case 'cancelled':
      case 'notify_failed': status = 'cancelled'; break;
      case 'processing': status = 'processing'; break;
      default: status = 'waitlist'; break;
    }
    return {
      id: a.alertId ?? 'alert-confirmation',
      date,
      time: timeLabel,
      status,
      statusLabel: a.statusLabel,
      statusColor: a.statusColor,
      courseName: a.course.name,
      location: a.course.location,
      locationId: a.course.locationId,
      apptTime,
      players: Array.from({ length: players }, () => ({})),
      holes,
      contact: a.user
        ? {
            name: a.user.name,
            email: a.user.email,
            phone: a.user.phone,
            avatarUrl: a.user.avatarUrl ?? undefined,
          }
        : undefined,
      isAlert: true,
      alertId: a.alertId,
      alertReferenceId: a.referenceId,
      expiresAt: a.expiresAt,
      notificationChannel: a.notificationChannel,
      notifiedAt: a.notifiedAt ?? undefined,
      requestedDate: isoDate,
    };
  }

  private mapWaitlistConfirmationToBooking(c: WaitlistConfirmationData): Booking {
    // New BE shape uses `criteria`; legacy waitlist-review shape uses `teeTime`. Read both.
    const criteria = c.criteria;
    const teeTime = c.teeTime;
    const isoDate = criteria?.isoDate ?? teeTime?.date;
    const apptTime = teeTime?.apptTime;
    const rawDate = apptTime ?? isoDate;
    const parsedDate = rawDate ? new Date(rawDate) : new Date();
    const date = Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
    const players = criteria?.players ?? teeTime?.players ?? 0;
    const holes = criteria?.holes ?? teeTime?.holes;
    const timeLabel = teeTime?.time ?? this.timeOfDayLabel(criteria?.timeOfDay);
    let status: BookingStatus;
    switch (c.status) {
      case 'confirmed': status = 'booked'; break;
      case 'expired':
      case 'cancelled':
      case 'canceled_by_user':
      case 'auto_confirm_failed':
      case 'failed': status = 'cancelled'; break;
      case 'processing': status = 'processing'; break;
      default: status = 'waitlist'; break;
    }
    return {
      id: c.waitlistId ?? 'waitlist-confirmation',
      date,
      time: timeLabel,
      status,
      courseName: c.course.name,
      location: c.course.location,
      locationId: c.course.locationId,
      apptTime,
      players: Array.from({ length: players }, () => ({})),
      holes,
      costLines: c.costSummary?.perPlayer.map((line) => ({
        label: line.label,
        amount: line.amount,
      })),
      bookingFee: c.costSummary?.bookingFee,
      totalPaid: c.costSummary?.totalDue,
      confirmationCode: c.confirmationNumber ?? undefined,
      contact: c.user
        ? {
            name: c.user.name,
            email: c.user.email,
            phone: c.user.phone,
            avatarUrl: c.user.avatarUrl ?? undefined,
          }
        : undefined,
      isWaitlist: true,
      waitlistId: c.waitlistId,
      waitlistReferenceId: c.referenceId,
      expiresAt: c.expiresAt,
      expiresInDays: c.expiresInDays,
      confirmedBookingId: c.confirmedBookingId ?? null,
      paymentType: c.paymentType,
      requestedDate: isoDate,
      timeOfDay: criteria?.timeOfDay,
    };
  }

  private timeOfDayLabel(t: string | undefined): string {
    if (!t || t === 'any') return 'Any time';
    return t.charAt(0).toUpperCase() + t.slice(1);
  }

  private mapConfirmationToBooking(c: BookingConfirmationData): Booking {
    const parsedDate = new Date(c.teeTime.apptTime);
    const coords = c.coordinates;
    return {
      id: c.bookingId ?? 'confirmation',
      date: Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate,
      time: c.teeTime.time,
      status: c.status === 'failed' ? 'cancelled' : 'booked',
      courseName: c.course.name,
      location: c.course.location,
      locationId: c.course.locationId,
      place: c.place,
      coordinates: coords && typeof coords.lat === 'number' && typeof coords.lng === 'number'
        ? { lat: coords.lat, lng: coords.lng }
        : undefined,
      apptTime: c.teeTime.apptTime,
      bookingId: c.bookingId,
      players: Array.from({ length: c.teeTime.players }, () => ({})),
      holes: c.teeTime.holes,
      costLines: c.costSummary?.perPlayer.map((line) => ({
        label: line.label,
        amount: line.amount,
      })),
      bookingFee: c.costSummary?.bookingFee,
      totalPaid: c.costSummary?.totalDue,
      confirmationCode: c.confirmationNumber ?? undefined,
      contact: c.user
        ? {
            name: c.user.name,
            email: c.user.email,
            phone: c.user.phone,
            avatarUrl: c.user.avatarUrl ?? undefined,
          }
        : undefined,
    };
  }
}
